import React, { useEffect, useState, useRef, createElement } from 'react';
import { XIcon, CheckCircleIcon, MailIcon, AlertCircleIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
// ─── Cloudflare Turnstile config ────────────────────────────────────────
// Feature flag: flip to `false` to disable the CAPTCHA gate entirely
// (e.g. emergency rollback) without removing any code. Mirrors the
// ENABLE_FORGOT_PASSWORD_CAPTCHA flag on the dedicated ForgotPassword page.
const ENABLE_FORGOT_PASSWORD_CAPTCHA = true;
// Cloudflare Turnstile PUBLIC site key. Safe to commit.
// The SECRET key lives in Firebase Secret Manager (TURNSTILE_SECRET_KEY)
// and is only readable by the verifyTurnstile Cloud Function.
const TURNSTILE_SITE_KEY = '0x4AAAAAADYnh4MLjOOURJjN';
// Cloudflare's Turnstile JS API URL. Loaded once per page via <script>.
const TURNSTILE_SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement | string, params: {
        sitekey: string;
        callback?: (token: string) => void;
        'expired-callback'?: () => void;
        'error-callback'?: () => void;
        theme?: 'light' | 'dark' | 'auto';
        size?: 'normal' | 'compact';
      }) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId?: string) => void;
    };
  }
}
interface PasswordResetModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export const PasswordResetModal = ({
  isOpen,
  onClose
}: PasswordResetModalProps) => {
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const {
    resetPassword
  } = useAuth();
  // ─── Turnstile state ───────────────────────────────────────────────────
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileContainerRef = useRef<HTMLDivElement | null>(null);
  const turnstileWidgetIdRef = useRef<string | null>(null);
  // ─── Load Turnstile script once ────────────────────────────────────────
  useEffect(() => {
    if (!ENABLE_FORGOT_PASSWORD_CAPTCHA) return;
    if (typeof window === 'undefined') return;
    if (window.turnstile) return;
    if (document.querySelector(`script[src="${TURNSTILE_SCRIPT_SRC}"]`)) return;
    const script = document.createElement('script');
    script.src = TURNSTILE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }, []);
  // ─── Render the Turnstile widget when the modal is open ────────────────
  // Depends on isOpen and isSubmitted so the widget is mounted only while
  // the form is visible. Cleans up the widget on close / success so
  // reopening the modal creates a fresh challenge each time.
  useEffect(() => {
    if (!ENABLE_FORGOT_PASSWORD_CAPTCHA) return;
    if (typeof window === 'undefined') return;
    if (!isOpen) return;
    if (isSubmitted) return; // Success view doesn't show the widget
    let cancelled = false;
    let intervalId: ReturnType<typeof setInterval> | null = null;
    const tryRender = () => {
      if (cancelled) return;
      const container = turnstileContainerRef.current;
      if (!container || !window.turnstile) return false;
      if (turnstileWidgetIdRef.current) return true; // already rendered
      try {
        const widgetId = window.turnstile.render(container, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (token: string) => {
            if (!cancelled) setTurnstileToken(token);
          },
          'expired-callback': () => {
            if (!cancelled) setTurnstileToken(null);
          },
          'error-callback': () => {
            if (!cancelled) setTurnstileToken(null);
          },
          theme: 'light'
        });
        turnstileWidgetIdRef.current = widgetId;
        return true;
      } catch (err) {
        console.error('[Turnstile] render failed:', err);
        return false;
      }
    };
    if (!tryRender()) {
      intervalId = setInterval(() => {
        if (tryRender() && intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
      }, 200);
    }
    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
      // Remove the widget on cleanup so reopening the modal mounts a
      // fresh challenge into a fresh DOM node.
      if (turnstileWidgetIdRef.current && window.turnstile?.remove) {
        try {
          window.turnstile.remove(turnstileWidgetIdRef.current);
        } catch (err) {
          console.warn('[Turnstile] remove failed:', err);
        }
      }
      turnstileWidgetIdRef.current = null;
    };
  }, [isOpen, isSubmitted]);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setError('Please enter your email address');
      return;
    }
    // ─── Cloudflare Turnstile gate ────────────────────────────────────
    // Verify BEFORE we call sendPasswordResetEmail. If verification fails
    // the request is aborted and no reset email is sent.
    if (ENABLE_FORGOT_PASSWORD_CAPTCHA) {
      if (!turnstileToken) {
        setError('Please complete the verification challenge before submitting.');
        return;
      }
      setIsLoading(true);
      const honeypotEl = document.getElementById('mc-prm-website-hp') as HTMLInputElement | null;
      const honeypot = honeypotEl?.value ?? '';
      const verifyFn = httpsCallable<{
        token: string;
        honeypot: string;
      }, {
        verified: boolean;
        errors?: string[];
      }>(functions, 'verifyTurnstile');
      let verifyResult;
      try {
        verifyResult = await verifyFn({
          token: turnstileToken,
          honeypot
        });
      } catch (verifyErr) {
        console.error('[Turnstile] verification call failed:', verifyErr);
        setError('Verification could not be completed. Please refresh the challenge and try again.');
        if (turnstileWidgetIdRef.current && window.turnstile?.reset) {
          window.turnstile.reset(turnstileWidgetIdRef.current);
        }
        setTurnstileToken(null);
        setIsLoading(false);
        return;
      }
      if (!verifyResult.data?.verified) {
        console.warn('[Turnstile] verification rejected:', verifyResult.data?.errors);
        setError('Verification failed. Please complete the challenge again and resubmit.');
        if (turnstileWidgetIdRef.current && window.turnstile?.reset) {
          window.turnstile.reset(turnstileWidgetIdRef.current);
        }
        setTurnstileToken(null);
        setIsLoading(false);
        return;
      }
    } else {
      setIsLoading(true);
    }
    try {
      // PasswordResetModal is only opened from Student Login, so we always
      // tag this reset as a 'student' flow. ResetPasswordPage uses this hint
      // to route the post-reset "Sign In with New Password" CTA back to
      // /student-login instead of the legacy /teacher-login default.
      await resetPassword(trimmedEmail, 'student');
      setIsSubmitted(true);
      // Clear token; the widget will be unmounted by the render effect
      // when isSubmitted flips to true.
      setTurnstileToken(null);
    } catch (err: any) {
      console.error('[PasswordResetModal] Password reset error:', err);
      const code = err?.code as string | undefined;
      if (code === 'auth/invalid-email') {
        setError('Please enter a valid email address.');
      } else if (code === 'auth/user-not-found') {
        // For security, avoid confirming whether an account exists.
        // Show the same success state as a real send.
        setIsSubmitted(true);
        setTurnstileToken(null);
      } else if (code === 'auth/too-many-requests') {
        setError('Too many attempts. Please wait a few minutes and try again.');
      } else if (code === 'auth/network-request-failed') {
        setError('Network error. Please check your internet connection and try again.');
      } else {
        setError('Failed to send password reset email. Please try again.');
      }
      // On any non-success outcome, refresh the widget so the next
      // attempt requires a new challenge.
      if (!isSubmitted && turnstileWidgetIdRef.current && window.turnstile?.reset) {
        window.turnstile.reset(turnstileWidgetIdRef.current);
        setTurnstileToken(null);
      }
    } finally {
      setIsLoading(false);
    }
  };
  const handleClose = () => {
    onClose();
    // Reset state after animation completes
    setTimeout(() => {
      setIsSubmitted(false);
      setEmail('');
      setError('');
      setTurnstileToken(null);
    }, 300);
  };
  if (!isOpen) return null;
  const submitDisabled = isLoading || ENABLE_FORGOT_PASSWORD_CAPTCHA && !turnstileToken;
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-10941">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-navy-900/80 backdrop-blur-sm" onClick={handleClose} data-id="element-10942"></div>
      {/* Modal */}
      <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-xl relative z-10 animate-fadeIn" data-id="element-10943">
        <div className="flex justify-between items-center mb-6" data-id="element-10944">
          <h3 className="text-2xl font-serif font-bold text-navy-900" data-id="element-10945">
            {isSubmitted ? 'Check Your Email' : 'Reset Password'}
          </h3>
          <button onClick={handleClose} className="text-gray-500 hover:text-gray-700 transition-colors" aria-label="Close" data-id="element-10946">
            <XIcon className="h-5 w-5" data-id="element-10947" />
          </button>
        </div>
        {!isSubmitted ? <>
            <p className="text-gray-600 mb-6" data-id="element-10948">
              Enter your email address and we'll send you instructions to reset
              your password.
            </p>
            {error && <div role="alert" className="mb-4 p-3 bg-red-50 border-l-4 border-red-500 text-red-700 flex items-start text-sm" data-id="element-10949">
                <AlertCircleIcon className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" data-id="element-10950" />
                <span data-id="element-10951">{error}</span>
              </div>}
            <form onSubmit={handleSubmit} data-id="element-10952">
              <div className="space-y-6" data-id="element-10953">
                <div data-id="element-10954">
                  <label htmlFor="reset-email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-10955">
                    Email Address
                  </label>
                  <input type="email" id="reset-email" value={email} onChange={e => setEmail(e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-deep-pink focus:border-deep-pink text-gray-900" placeholder="you@example.com" required disabled={isLoading} autoComplete="email" data-id="element-10956" />
                </div>

                {/* ─── Honeypot (visually hidden, real users never fill) ─── */}
                {ENABLE_FORGOT_PASSWORD_CAPTCHA && <div aria-hidden="true" style={{
              position: 'absolute',
              left: '-10000px',
              top: 'auto',
              width: '1px',
              height: '1px',
              overflow: 'hidden'
            }} data-id="element-10957">
                    <label htmlFor="mc-prm-website-hp" data-id="element-10958">
                      Website (leave blank)
                    </label>
                    <input id="mc-prm-website-hp" name="website" type="text" tabIndex={-1} autoComplete="off" defaultValue="" data-id="element-10959" />
                  </div>}

                {/* ─── Cloudflare Turnstile widget ─── */}
                {ENABLE_FORGOT_PASSWORD_CAPTCHA && <div data-id="element-10960">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-10961">
                      Human Verification
                    </label>
                    <div className="flex justify-center" data-id="element-10962">
                      <div ref={turnstileContainerRef} data-id="element-10963" />
                    </div>
                  </div>}

                <button type="submit" disabled={submitDisabled} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors flex justify-center items-center disabled:opacity-70 disabled:cursor-not-allowed" data-id="element-10964">
                  {isLoading ? <span className="inline-block h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" data-id="element-10965"></span> : null}
                  {isLoading ? 'Sending...' : 'Send Reset Instructions'}
                </button>
              </div>
            </form>
          </> : <div className="text-center" data-id="element-10966">
            <div className="flex justify-center mb-4" data-id="element-10967">
              <div className="bg-green-100 p-3 rounded-full" data-id="element-10968">
                <CheckCircleIcon className="h-10 w-10 text-green-500" data-id="element-10969" />
              </div>
            </div>
            <p className="text-gray-600 mb-4" data-id="element-10970">
              Reset instructions have been sent to:
            </p>
            <div className="bg-gray-100 rounded-lg p-3 mb-6 flex items-center justify-center gap-2" data-id="element-10971">
              <MailIcon className="h-5 w-5 text-gray-500" data-id="element-10972" />
              <span className="font-medium text-gray-800" data-id="element-10973">{email}</span>
            </div>
            <p className="text-sm text-gray-500 mb-6" data-id="element-10974">
              Please check your email and follow the instructions to reset your
              password. If you don't see the email, check your spam folder.
            </p>
            <button onClick={handleClose} className="w-full bg-deep-pink hover:bg-burgundy text-white font-medium py-3 px-4 rounded-lg transition-colors" data-id="element-10975">
              Close
            </button>
          </div>}
      </div>
    </div>;
};